import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance for limits API - use same base URL as main API
const limitsApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token
limitsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
limitsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Don't automatically redirect on 401 - let the component handle it
    // This prevents the "Logging you in..." behavior when limits aren't configured
    return Promise.reject(error);
  }
);

export interface OrganizationLimitsData {
  credit_soft_limit: number;
  credit_hard_limit: number;
  credit_hard_limit_enabled: boolean;
  credit_pool_monthly?: number;
  credit_pool_yearly?: number;
  credit_pool_type: 'monthly' | 'yearly';
  security_access: boolean;
  devops_access: boolean;
  ide_access: boolean;
  web_access: boolean;
  auto_topup_enabled: boolean;
  auto_topup_amount: number;
  auto_topup_threshold: number;
  notification_threshold_80: boolean;
  notification_threshold_90: boolean;
  notification_threshold_95: boolean;
}

export interface UserLimitsData {
  credit_soft_limit: number;
  credit_hard_limit: number;
  credit_hard_limit_enabled: boolean;
  security_access: boolean;
  devops_access: boolean;
  ide_access: boolean;
  web_access: boolean;
}

export interface CreditCheckResponse {
  allowed: boolean;
  reason: string;
  current_usage: number;
  remaining: number;
  limit_value?: number;
}

export interface FeatureAccessResponse {
  allowed: boolean;
  reason: string;
}

export interface CreditPoolStatus {
  pool_type: 'monthly' | 'yearly';
  total_pool: number;
  remaining: number;
  usage_percentage: number;
  auto_topup_enabled: boolean;
}

export interface LimitNotification {
  id: number;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  read: boolean;
  created_at: string;
  notification_type: 'credit_warning' | 'credit_exceeded' | 'feature_blocked' | 'pool_low' | 'auto_topup';
  user_id?: number;
  org_id?: number;
}

export interface EnforcementHistoryItem {
  id: number;
  user_id: number;
  org_id: number;
  limit_type: string;
  action_taken: string;
  reason: string;
  created_at: string;
  user_email?: string;
  org_name?: string;
}

export interface LimitOverrideData {
  user_id: number;
  org_id: number;
  override_reason: string;
  override_expires_at: string;
}

export interface TopupData {
  amount: number;
  topup_type: 'manual' | 'auto';
  reason: string;
}

// Organization Limits Management (Superadmin Only)
export const organizationLimitsApi = {
  // Create/Update Organization Limits
  createOrgLimits: async (orgId: number, limitsData: OrganizationLimitsData) => {
    const response = await limitsApi.post(`/limits/organizations/${orgId}/limits`, limitsData);
    return response.data;
  },

  // Get Organization Limits
  getOrgLimits: async (orgId: number) => {
    const response = await limitsApi.get(`/limits/organizations/${orgId}/limits`);
    return response.data;
  },

  // Update Organization Limits
  updateOrgLimits: async (orgId: number, limitsData: Partial<OrganizationLimitsData>) => {
    const response = await limitsApi.put(`/limits/organizations/${orgId}/limits`, limitsData);
    return response.data;
  },
};

// User Limits Management (Org Admin)
export const userLimitsApi = {
  // Create/Update User Limits
  createUserLimits: async (userId: number, orgId: number, limitsData: UserLimitsData) => {
    const response = await limitsApi.post(`/limits/users/${userId}/limits?org_id=${orgId}`, limitsData);
    return response.data;
  },

  // Get User Limits
  getUserLimits: async (userId: number, orgId: number) => {
    const response = await limitsApi.get(`/limits/users/${userId}/limits?org_id=${orgId}`);
    return response.data;
  },

  // Update User Limits
  updateUserLimits: async (userId: number, orgId: number, limitsData: Partial<UserLimitsData>) => {
    const response = await limitsApi.put(`/limits/users/${userId}/limits?org_id=${orgId}`, limitsData);
    return response.data;
  },
};

// Real-time Limit Checking (Critical for UX)
export const limitCheckApi = {
  // Check User Credit Limit
  checkUserCredit: async (userId: number, orgId: number, creditAmount: number): Promise<CreditCheckResponse> => {
    const response = await limitsApi.post(`/limits/check/user-credit?user_id=${userId}&org_id=${orgId}&credit_amount=${creditAmount}`);
    return response.data;
  },

  // Check Feature Access
  checkFeatureAccess: async (userId: number, orgId: number, feature: string): Promise<FeatureAccessResponse> => {
    const response = await limitsApi.post(`/limits/check/feature-access?user_id=${userId}&org_id=${orgId}&feature=${feature}`);
    return response.data;
  },
};

// Credit Pool Management
export const creditPoolApi = {
  // Get Credit Pool Status
  getCreditPoolStatus: async (orgId: number): Promise<CreditPoolStatus> => {
    const response = await limitsApi.get(`/limits/organizations/${orgId}/credit-pool`);
    return response.data;
  },

  // Create Credit Pool Top-up (Superadmin Only)
  createTopup: async (orgId: number, topupData: TopupData) => {
    const response = await limitsApi.post(`/limits/organizations/${orgId}/credit-pool/topup`, topupData);
    return response.data;
  },
};

// Notifications System
export const notificationsApi = {
  // Get User Notifications
  getNotifications: async (orgId: number, unreadOnly: boolean = false): Promise<LimitNotification[]> => {
    const response = await limitsApi.get(`/limits/notifications?org_id=${orgId}&unread_only=${unreadOnly}`);
    return response.data;
  },

  // Mark Notification as Read
  markNotificationRead: async (notificationId: number) => {
    const response = await limitsApi.put(`/limits/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark All Notifications as Read
  markAllNotificationsRead: async (orgId: number) => {
    const response = await limitsApi.put(`/limits/notifications/mark-all-read?org_id=${orgId}`);
    return response.data;
  },
};

// Enforcement History
export const enforcementApi = {
  // Get Enforcement History
  getEnforcementHistory: async (orgId?: number, userId?: number, limit: number = 100): Promise<EnforcementHistoryItem[]> => {
    let url = `/limits/enforcement-history?limit=${limit}`;
    if (orgId) url += `&org_id=${orgId}`;
    if (userId) url += `&user_id=${userId}`;
    
    const response = await limitsApi.get(url);
    return response.data;
  },
};

// Limit Override (Superadmin Only)
export const overrideApi = {
  // Apply Limit Override
  applyLimitOverride: async (overrideData: LimitOverrideData) => {
    const response = await limitsApi.post('/limits/override', overrideData);
    return response.data;
  },

  // Get Active Overrides
  getActiveOverrides: async (orgId?: number) => {
    let url = '/limits/override/active';
    if (orgId) url += `?org_id=${orgId}`;
    
    const response = await limitsApi.get(url);
    return response.data;
  },

  // Remove Override
  removeOverride: async (overrideId: number) => {
    const response = await limitsApi.delete(`/limits/override/${overrideId}`);
    return response.data;
  },
};

// Error handling utility
export const handleLimitError = (error: any, action: string) => {
  if (error.response?.status === 403) {
    return 'Insufficient permissions for this action';
  } else if (error.response?.status === 422) {
    return 'Credit limit exceeded. Please contact your administrator.';
  } else if (error.response?.status === 404) {
    return 'Limits not configured. Please contact support.';
  } else {
    return `Failed to ${action}. Please try again.`;
  }
};

export default limitsApi;
